import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "./Header";
import Footer from "./Footer";

import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationsRead,
  selectNotificationsItems,
  selectNotificationsStatus,
  selectUnreadCount,
  selectNotificationsError,
  selectMarkStatus,
} from "../redux/slices/notificationsSlice";

function Notifications() {
  const dispatch = useDispatch();

  const items = useSelector(selectNotificationsItems) || [];
  const status = useSelector(selectNotificationsStatus);
  const unreadCount = useSelector(selectUnreadCount);
  const errorMsg = useSelector(selectNotificationsError);
  const markStatus = useSelector(selectMarkStatus);

  const loading = status === "loading";
  const marking = markStatus === "loading";

  const [selectedIds, setSelectedIds] = useState({});

  useEffect(() => {
    dispatch(fetchNotifications());
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  // helpers
  const getId = (n) =>
    n?.notificationId || n?.notificationID || n?.id || n?._id || "";
  const getTitle = (n) =>
    n?.title || n?.subject || n?.heading || "Notification";
  const getMessage = (n) =>
    n?.message || n?.body || n?.text || n?.description || "";
  const getDateRaw = (n) =>
    n?.createdAt || n?.date || n?.timestamp || n?.updatedAt;

  const isRead = (n) => {
    if (n?.isRead === true || n?.read === true) return true;
    const s = String(n?.status || "").toLowerCase();
    return s === "read";
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatTimeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const diffMs = Date.now() - d.getTime();

    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return formatDate(iso);
  };

  const selectedList = useMemo(
    () =>
      Object.entries(selectedIds)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [selectedIds],
  );

  const toggleOne = (id) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectAllUnread = () => {
    const next = {};
    for (const n of items) {
      const id = getId(n);
      if (!id) continue;
      if (!isRead(n)) next[id] = true;
    }
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds({});

  const refresh = () => {
    dispatch(fetchNotifications());
    dispatch(fetchUnreadCount());
  };

  const markSelectedRead = async () => {
    if (selectedList.length === 0) return;
    const res = await dispatch(
      markNotificationsRead({ notificationIds: selectedList }),
    );
    if (res?.type?.endsWith("/fulfilled")) {
      clearSelection();
      dispatch(fetchUnreadCount());
    }
  };

  const markOneRead = async (id) => {
    if (!id) return;
    const res = await dispatch(
      markNotificationsRead({ notificationIds: [id] }),
    );
    if (res?.type?.endsWith("/fulfilled")) {
      dispatch(fetchUnreadCount());
      setSelectedIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <>
      <Header />

      <main className="pt-2 pb-2">
        <section className="container">
          <div className="row pt-3">
            <div className="d-flex justify-content-between mb-3">
              <div className="w-100">
                <span className="text-uppercase text-secondary fw-semibold fs-6 ls-md">
                  NOTIFICATIONS
                </span>
                <h2 className="fw-semibold ls-sm text-success fs-3 lh-1">
                  Keep Up With Your Latest Updates
                </h2>
                <div className="mt-n3 pt-2">
                  <img src="/assets/images/line-shap.webp" width="150" alt="" />
                </div>
              </div>
              <div
                className="circle-button bg-success-soft rounded-circle px-2 py-2"
                type="button"
                onClick={refresh}
                disabled={loading}
                title="Refresh"
              >
                <i className="bi bi-arrow-clockwise text-dark"></i>
              </div>
            </div>
          </div>

          {errorMsg ? (
            <div className="alert alert-danger py-2 mt-3">{errorMsg}</div>
          ) : null}

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div
                className="spinner-border"
                role="status"
                aria-hidden="true"
              />
            </div>
          ) : null}

          {!loading && items.length === 0 ? (
            <div className="card rounded-4 border border-light shadow-sm mt-3">
              <div className="card-body p-5">
                <h5 className="lh-1 mb-0">No Notifications</h5>
                <p className="text-muted mb-0">You’re all caught up.</p>
              </div>
            </div>
          ) : null}

          {/* Twitter-like Feed */}
          <div>
            {!loading &&
              items.map((n, idx) => {
                const id = getId(n) || String(idx);
                const unread = !isRead(n);
                const checked = !!selectedIds[id];
                const date = getDateRaw(n);
                const title = getTitle(n);
                const msg = getMessage(n);

                return (
                  <div
                    key={id}
                    className="py-3 px-5 rounded-4 mb-1"
                    style={{
                      transition: "background 120ms ease",
                      background: unread
                        ? "rgba(255, 202, 202, 0.25)"
                        : "rgba(194, 194, 194, 0.17)",
                    }}
                  >
                    <div className="d-flex gap-3">
                      {/* unread dot */}
                      <div className="pt-1" style={{ width: 16 }}>
                        <span
                          className="d-inline-block rounded-circle"
                          style={{
                            width: 10,
                            height: 10,
                            background: unread ? "#0d6efd" : "transparent",
                            border: unread ? "none" : "1px solid #dee2e6",
                          }}
                          title={unread ? "Unread" : "Read"}
                        />
                      </div>

                      {/* content */}
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start gap-3">
                          <div className="d-flex align-items-start gap-2">
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <div className="fw-semibold">{title}</div>
                                {unread ? (
                                  <span className="badge rounded-pill text-bg-primary px-3 fs-7">
                                    Unread
                                  </span>
                                ) : (
                                  <span className="badge rounded-pill text-bg-light text-dark border px-3 fs-7">
                                    Read
                                  </span>
                                )}
                              </div>

                              {msg ? (
                                <div
                                  className="text-muted"
                                  style={{ lineHeight: 1.35 }}
                                >
                                  {msg}
                                </div>
                              ) : null}
                            </div>
                          </div>

                          {/* right meta + action */}
                          <div className="text-end" style={{ minWidth: 140 }}>
                            <div
                              className="text-muted small lh-1"
                              title={date ? String(date) : ""}
                              style={{ whiteSpace: "nowrap" }}
                            >
                              {formatTimeAgo(date)}
                            </div>

                            <button
                              className="btn btn-link fw-medium btn-sm px-0 lh-1 mt-n2"
                              type="button"
                              onClick={() => markOneRead(id)}
                              disabled={!unread || marking}
                              style={{ textDecoration: "none" }}
                            >
                              {unread ? "Mark as read" : ""}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Notifications;
